import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useRef,
} from "react";
import {
  Popover,
  Typography,
  Box,
  Button,
  IconButton,
  CircularProgress,
  TextField,
  Chip,
} from "@material-ui/core";
import { makeStyles, alpha } from "@material-ui/core/styles";
import {
  ShieldAlert,
  Check,
  CheckCircle2,
  X,
  Clock,
  Globe,
  AlertTriangle,
  ExternalLink,
} from "lucide-react";
import IconButtonWithTooltip from "../ui/IconButtonWithTooltip";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useToast } from "../../hooks/useToast";
import {
  listPendingActions,
  approvePendingAction,
  rejectPendingAction,
} from "../../services/pendingActionsApi";
import StandardModal from "../ui/StandardModal";
import soundService from "../../services/soundService";

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    popoverPaper: {
      padding: 0,
      borderRadius: 16,
      maxWidth: 400,
      maxHeight: 520,
      overflow: "hidden",
      boxShadow: dark
        ? "0 8px 32px rgba(0,0,0,0.4)"
        : "0 8px 32px rgba(0,0,0,0.12)",
      border: `1px solid ${theme.palette.divider}`,
      marginTop: theme.spacing(1),
    },
    header: {
      background: `linear-gradient(135deg, #f59e0b 0%, #d97706 100%)`,
      color: "#fff",
      padding: theme.spacing(0.875, 1.25),
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(0.75),
    },
    headerIcon: {
      width: 24,
      height: 24,
      borderRadius: 5,
      backgroundColor: "rgba(255,255,255,0.15)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    headerCount: {
      fontWeight: 700,
      fontSize: "0.65rem",
      backgroundColor: "rgba(255,255,255,0.2)",
      color: "#fff",
      borderRadius: 8,
      padding: "1px 7px",
      marginLeft: 6,
    },
    closeButton: {
      color: "rgba(255,255,255,0.7)",
      padding: 2,
      "&:hover": {
        color: "#fff",
        backgroundColor: "rgba(255,255,255,0.1)",
      },
    },
    list: {
      maxHeight: 420,
      overflowY: "auto",
      padding: "4px 0",
    },
    actionItem: {
      padding: "12px 16px",
      borderBottom: `1px solid ${theme.palette.divider}`,
      transition: "background-color 0.15s",
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
      },
      "&:last-child": {
        borderBottom: "none",
      },
    },
    actionSummary: {
      fontSize: "0.85rem",
      fontWeight: 600,
      color: theme.palette.text.primary,
      lineHeight: 1.4,
      marginBottom: 4,
    },
    actionMeta: {
      display: "flex",
      alignItems: "center",
      gap: 4,
      marginBottom: 8,
      flexWrap: "wrap",
    },
    domainChip: {
      height: 22,
      fontSize: "0.7rem",
      backgroundColor: dark
        ? alpha("#3b82f6", 0.15)
        : alpha("#3b82f6", 0.08),
      color: dark ? "#60a5fa" : "#2563eb",
      fontWeight: 600,
      "& .MuiChip-icon": {
        color: `${dark ? "#60a5fa" : "#2563eb"} !important`,
      },
    },
    timerChip: {
      height: 22,
      fontSize: "0.7rem",
      backgroundColor: dark
        ? alpha("#f59e0b", 0.15)
        : alpha("#f59e0b", 0.08),
      color: dark ? "#fbbf24" : "#d97706",
      fontWeight: 600,
      "& .MuiChip-icon": {
        color: `${dark ? "#fbbf24" : "#d97706"} !important`,
      },
    },
    expiredChip: {
      height: 22,
      fontSize: "0.7rem",
      backgroundColor: dark
        ? alpha("#ef4444", 0.15)
        : alpha("#ef4444", 0.08),
      color: dark ? "#f87171" : "#dc2626",
      fontWeight: 600,
      "& .MuiChip-icon": {
        color: `${dark ? "#f87171" : "#dc2626"} !important`,
      },
    },
    ticketChip: {
      height: 22,
      fontSize: "0.7rem",
      backgroundColor: dark
        ? alpha("#8b5cf6", 0.15)
        : alpha("#8b5cf6", 0.08),
      color: dark ? "#a78bfa" : "#7c3aed",
      fontWeight: 600,
    },
    buttonRow: {
      display: "flex",
      gap: 8,
    },
    approveBtn: {
      flex: 1,
      textTransform: "none",
      fontWeight: 700,
      fontSize: "0.8rem",
      borderRadius: 8,
      backgroundColor: dark
        ? alpha("#10b981", 0.15)
        : alpha("#10b981", 0.1),
      color: dark ? "#34d399" : "#059669",
      "&:hover": {
        backgroundColor: dark
          ? alpha("#10b981", 0.25)
          : alpha("#10b981", 0.2),
      },
    },
    rejectBtn: {
      flex: 1,
      textTransform: "none",
      fontWeight: 700,
      fontSize: "0.8rem",
      borderRadius: 8,
      backgroundColor: dark
        ? alpha("#ef4444", 0.15)
        : alpha("#ef4444", 0.1),
      color: dark ? "#f87171" : "#dc2626",
      "&:hover": {
        backgroundColor: dark
          ? alpha("#ef4444", 0.25)
          : alpha("#ef4444", 0.2),
      },
    },
    emptyState: {
      padding: theme.spacing(3),
      textAlign: "center",
      color: theme.palette.text.secondary,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: theme.spacing(0.5),
    },
    emptyIcon: {
      opacity: 0.2,
      marginBottom: 4,
    },
    rejectField: {
      marginTop: 12,
      "& .MuiOutlinedInput-root": {
        borderRadius: 8,
        backgroundColor: theme.palette.background.paper,
      },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.divider },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
    },
    "& .MuiInputLabel-root": { color: theme.palette.text.secondary },
    "& .MuiInputLabel-shrink": {
      backgroundColor: theme.palette.background.paper,
      padding: "0 6px",
      marginLeft: -4,
      borderRadius: 4,
    },
    },
    detailRow: {
      display: "flex",
      gap: 8,
      marginBottom: 8,
      alignItems: "flex-start",
    },
    detailLabel: {
      fontWeight: 700,
      fontSize: "0.8rem",
      color: theme.palette.text.secondary,
      minWidth: 70,
      flexShrink: 0,
    },
    detailValue: {
      fontSize: "0.85rem",
      color: theme.palette.text.primary,
      wordBreak: "break-word",
    },
    warningBox: {
      padding: "10px 14px",
      borderRadius: 10,
      backgroundColor: dark
        ? alpha("#f59e0b", 0.12)
        : alpha("#f59e0b", 0.08),
      border: `1px solid ${dark ? alpha("#f59e0b", 0.3) : alpha("#f59e0b", 0.2)}`,
      display: "flex",
      gap: 8,
      alignItems: "flex-start",
      marginTop: 12,
    },
    warningText: {
      fontSize: "0.8rem",
      color: theme.palette.text.primary,
      lineHeight: 1.5,
    },
  };
});

function getTimeRemaining(expiresAt) {
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return "Expirado";
  const minutes = Math.ceil(diff / 60000);
  if (minutes < 1) return "< 1 min";
  return `${minutes} min`;
}

function isExpired(expiresAt) {
  return new Date(expiresAt) <= new Date();
}

const PendingActionsPopover = () => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const anchorRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [, setTick] = useState(0);

  const [detailAction, setDetailAction] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);

  // F6 fix: estado para exibir erro de polling (antes silent fail completo)
  const [pollingError, setPollingError] = useState(null);
  const lastErrorToastRef = useRef(0);

  const fetchActions = useCallback(async (showLoading = false) => {
    try {
      if (showLoading) setLoading(true);
      const data = await listPendingActions("pending");
      setActions(data);
      // Sucesso → limpa erro anterior
      if (pollingError) setPollingError(null);
    } catch (err) {
      // F6 fix: ao invés de silent fail, expor ao user.
      // Toast no máximo 1x a cada 5 minutos para não poluir UI.
      const now = Date.now();
      if (now - lastErrorToastRef.current > 5 * 60 * 1000) {
        lastErrorToastRef.current = now;
        try {
          const message = err?.response?.data?.error || err?.message || "Falha ao buscar ações pendentes";
          // Usar toastRef (atualizado a cada render via toastRef.current = toast)
          // para evitar incluir `toast` nas deps do useCallback — caso contrário
          // o callback recriaria a cada render, derrubando o setInterval do polling.
          toastRef.current?.warning?.(message, { autoClose: 5000 });
        } catch {
          // ignore
        }
      }
      setPollingError(err?.message || "Falha ao buscar ações pendentes");
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [pollingError]);

  useEffect(() => {
    fetchActions(true);
    const interval = setInterval(() => fetchActions(false), 30000);
    return () => clearInterval(interval);
  }, [fetchActions]);

  useEffect(() => {
    if (!open || actions.length === 0) return;
    const timer = setInterval(() => setTick((t) => t + 1), 15000);
    return () => clearInterval(timer);
  }, [open, actions.length]);

  useEffect(() => {
    if (!socket || !user?.companyId) return;

    const event = `company-${user.companyId}-pendingAction`;

    const handleEvent = (data) => {
      if (data.action === "create") {
        fetchActions(false);
        toastRef.current.warning(
          `⚠️ Ação de IA requer aprovação: ${data.pendingAction?.actionSummary || "Ação pendente"}`,
          { autoHideDuration: 8000 }
        );
        try {
          soundService.play();
        } catch {
          // Silent
        }
      } else if (
        data.action === "update" ||
        data.action === "executed" ||
        data.action === "expired"
      ) {
        fetchActions(false);
      }
    };

    socket.on(event, handleEvent);
    return () => socket.off(event, handleEvent);
  }, [socket, user?.companyId, fetchActions]);

  const handleApprove = async (actionId) => {
    setProcessingId(actionId);
    try {
      const result = await approvePendingAction(actionId);
      if (result.executionResult?.success) {
        toast.success("Ação aprovada e executada com sucesso!");
      } else {
        toast.warning("Ação aprovada mas a execução falhou. Verifique os logs.");
      }
      fetchActions(false);
      setDetailAction(null);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Erro ao aprovar a ação");
    } finally {
      setProcessingId(null);
    }
  };

  const handleOpenReject = (action) => {
    setRejectTarget(action);
    setRejectReason("");
    setShowRejectModal(true);
  };

  const handleConfirmReject = async () => {
    if (!rejectTarget) return;
    setProcessingId(rejectTarget.id);
    try {
      await rejectPendingAction(rejectTarget.id, rejectReason);
      toast.info("Ação rejeitada.");
      fetchActions(false);
      setShowRejectModal(false);
      setRejectTarget(null);
      setDetailAction(null);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Erro ao rejeitar a ação");
    } finally {
      setProcessingId(null);
    }
  };

  const validActions = actions.filter(
    (a) => a.status === "pending" && !isExpired(a.expiresAt)
  );
  const pendingCount = validActions.length;

  return (
    <>
      <div ref={anchorRef}>
        <IconButtonWithTooltip
          icon={ShieldAlert}
          tooltip="Ações Pendentes de Aprovação"
          onClick={() => {
            setOpen(true);
            fetchActions(true);
          }}
          aria-label="Ações pendentes de aprovação"
          badgeProps={
            pendingCount > 0
              ? {
                  badgeContent: pendingCount,
                  color: "error",
                  overlap: "rectangular",
                }
              : undefined
          }
        />
      </div>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ className: classes.popoverPaper }}
      >
        <Box className={classes.header}>
          <Box className={classes.headerLeft}>
            <div className={classes.headerIcon}>
              <ShieldAlert size={14} />
            </div>
            <Typography style={{ fontSize: "0.8rem", fontWeight: 600, color: "#fff" }}>
              Aprovação de Ações
            </Typography>
            {pendingCount > 0 && (
              <Typography className={classes.headerCount}>
                {pendingCount}
              </Typography>
            )}
          </Box>
          <IconButton
            size="small"
            onClick={() => setOpen(false)}
            className={classes.closeButton}
          >
            <X size={14} />
          </IconButton>
        </Box>

        <Box className={classes.list}>
          {loading && validActions.length === 0 ? (
            <Box className={classes.emptyState}>
              <CircularProgress size={24} />
            </Box>
          ) : validActions.length === 0 ? (
            <Box className={classes.emptyState}>
              <CheckCircle2 size={32} className={classes.emptyIcon} />
              <Typography style={{ fontSize: "0.85rem", fontWeight: 500 }}>
                Nenhuma ação pendente
              </Typography>
              <Typography variant="caption" color="textSecondary" style={{ fontSize: "0.75rem" }}>
                Ações que necessitam aprovação aparecerão aqui
              </Typography>
            </Box>
          ) : (
            validActions.map((action) => {
              const expired = isExpired(action.expiresAt);
              return (
                <Box key={action.id} className={classes.actionItem}>
                  <Typography className={classes.actionSummary}>
                    {action.actionSummary}
                  </Typography>
                  <Box className={classes.actionMeta}>
                    {action.targetDomain && (
                      <Chip
                        size="small"
                        icon={<Globe size={12} />}
                        label={action.targetDomain}
                        className={classes.domainChip}
                      />
                    )}
                    <Chip
                      size="small"
                      icon={<Clock size={12} />}
                      label={getTimeRemaining(action.expiresAt)}
                      className={expired ? classes.expiredChip : classes.timerChip}
                    />
                    {action.ticketId && (
                      <Chip
                        size="small"
                        label={`#${action.ticketId}`}
                        className={classes.ticketChip}
                      />
                    )}
                  </Box>
                  <Box className={classes.buttonRow}>
                    <Button
                      size="small"
                      className={classes.approveBtn}
                      startIcon={
                        processingId === action.id ? (
                          <CircularProgress size={14} />
                        ) : (
                          <Check size={14} />
                        )
                      }
                      disabled={processingId === action.id || expired}
                      onClick={() => setDetailAction(action)}
                      aria-label={`Revisar e aprovar: ${action.actionSummary}`}
                    >
                      Revisar & Aprovar
                    </Button>
                    <Button
                      size="small"
                      className={classes.rejectBtn}
                      startIcon={<X size={14} />}
                      disabled={processingId === action.id || expired}
                      onClick={() => handleOpenReject(action)}
                      aria-label={`Rejeitar: ${action.actionSummary}`}
                    >
                      Rejeitar
                    </Button>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </Popover>

      <StandardModal
        title="Revisar Ação"
        open={!!detailAction}
        onClose={() => setDetailAction(null)}
        onConfirm={() => handleApprove(detailAction?.id)}
        maxWidth="sm"
        closeOnConfirm={false}
        titleIcon={<ShieldAlert size={22} />}
        primaryButton={{
          text: "Aprovar e Executar",
          color: "primary",
          disabled: processingId === detailAction?.id,
          loading: processingId === detailAction?.id,
          startIcon: <Check size={16} />,
        }}
        secondaryButton={{
          text: "Cancelar",
          color: "secondary",
          disabled: !!processingId,
        }}
      >
        {detailAction && (
          <Box>
            <Box className={classes.detailRow}>
              <Typography className={classes.detailLabel}>Ação:</Typography>
              <Typography className={classes.detailValue}>
                {detailAction.actionSummary}
              </Typography>
            </Box>
            <Box className={classes.detailRow}>
              <Typography className={classes.detailLabel}>Intent:</Typography>
              <Typography className={classes.detailValue}>
                {detailAction.intent}
              </Typography>
            </Box>
            {detailAction.targetUrl && (
              <Box className={classes.detailRow}>
                <Typography className={classes.detailLabel}>URL:</Typography>
                <Typography className={classes.detailValue}>
                  {detailAction.targetUrl}{" "}
                  <ExternalLink
                    size={12}
                    style={{ verticalAlign: "middle", opacity: 0.6 }}
                  />
                </Typography>
              </Box>
            )}
            {detailAction.targetDomain && (
              <Box className={classes.detailRow}>
                <Typography className={classes.detailLabel}>Site:</Typography>
                <Chip
                  size="small"
                  icon={<Globe size={12} />}
                  label={detailAction.targetDomain}
                  className={classes.domainChip}
                />
              </Box>
            )}
            {detailAction.ticket && (
              <Box className={classes.detailRow}>
                <Typography className={classes.detailLabel}>Ticket:</Typography>
                <Typography className={classes.detailValue}>
                  #{detailAction.ticketId}
                  {detailAction.contact?.name &&
                    ` — ${detailAction.contact.name}`}
                </Typography>
              </Box>
            )}
            <Box className={classes.detailRow}>
              <Typography className={classes.detailLabel}>Expira:</Typography>
              <Chip
                size="small"
                icon={<Clock size={12} />}
                label={getTimeRemaining(detailAction.expiresAt)}
                className={
                  isExpired(detailAction.expiresAt)
                    ? classes.expiredChip
                    : classes.timerChip
                }
              />
            </Box>

            <Box className={classes.warningBox}>
              <AlertTriangle size={18} style={{ flexShrink: 0, color: "#f59e0b", marginTop: 1 }} />
              <Typography className={classes.warningText}>
                Ao aprovar, a IA executará uma <strong>ação de escrita</strong> no
                sistema externo ({detailAction.targetDomain}). Essa operação pode
                criar, modificar ou excluir dados. Certifique-se de que a ação é
                a desejada.
              </Typography>
            </Box>
          </Box>
        )}
      </StandardModal>

      <StandardModal
        title="Rejeitar Ação"
        open={showRejectModal}
        onClose={() => {
          setShowRejectModal(false);
          setRejectTarget(null);
        }}
        onConfirm={handleConfirmReject}
        maxWidth="xs"
        closeOnConfirm={false}
        titleIcon={<X size={22} />}
        primaryButton={{
          text: "Confirmar Rejeição",
          color: "secondary",
          disabled: processingId === rejectTarget?.id,
          loading: processingId === rejectTarget?.id,
        }}
        secondaryButton={{
          text: "Cancelar",
          color: "default",
          disabled: !!processingId,
        }}
      >
        <Typography variant="body2" style={{ marginBottom: 8 }}>
          {rejectTarget?.actionSummary}
        </Typography>
        <TextField
          className={classes.rejectField}
          label="Motivo da rejeição (opcional)"
          variant="outlined"
          fullWidth
          multiline
          rows={3}
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="Ex: O cliente desistiu do agendamento"
        />
      </StandardModal>
    </>
  );
};

export default PendingActionsPopover;
